import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { fetchProducts, SHOP_COLORS, FABRICS, OCCASIONS } from "../lib/api";
import { useSEO } from "../lib/seo";
import { metaTrack } from "../analytics/metaPixel";
import { ProductCard } from "../components/ProductCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Slider } from "../components/ui/slider";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const CATEGORY_LABELS = {
  traditional: "Traditional Ilkal Sarees",
  cotton: "Ilkal Cotton Sarees",
  silk: "Ilkal Silk Sarees",
  "patteda-anchu": "Patteda Anchu Sarees",
  daily: "Daily Wear",
  premium: "Premium Collection",
};

const CHIP_LABELS = {
  traditional: "Traditional",
  cotton: "Cotton",
  silk: "Silk",
  "patteda-anchu": "Patteda Anchu",
  daily: "Daily Wear",
  premium: "Premium",
};

const SORTS = [
  { value: "featured", label: "Featured" },
  { value: "newest", label: "Newest" },
  { value: "best-selling", label: "Best Selling" },
  { value: "price-asc", label: "Price: Low to High" },
  { value: "price-desc", label: "Price: High to Low" },
];

export default function ShopPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [price, setPrice] = useState([0, 50000]);
  const [colors, setColors] = useState([]);
  const [fabrics, setFabrics] = useState([]);
  const [occasions, setOccasions] = useState([]);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [sort, setSort] = useState("featured");

  const category = searchParams.get("category") || "";
  const badge = searchParams.get("badge") || "";
  const q = searchParams.get("q") || "";
  const paramColor = searchParams.get("color") || "";

  useEffect(() => {
    if (paramColor) setColors([paramColor]);
  }, [paramColor]);

  const params = useMemo(() => {
    const p = { sort };
    if (q) p.q = q;
    if (category) p.category = category;
    if (badge) p.badge = badge;
    if (colors.length === 1) p.color = colors[0];
    if (fabrics.length === 1) p.fabric = fabrics[0];
    if (occasions.length === 1) p.occasion = occasions[0];
    if (inStockOnly) p.in_stock = true;
    if (price[0] > 0) p.min_price = price[0];
    if (price[1] < 50000) p.max_price = price[1];
    return p;
  }, [sort, q, category, badge, colors, fabrics, occasions, inStockOnly, price]);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      fetchProducts(params)
        .then((data) => {
          let d = data;
          if (colors.length > 1) d = d.filter((x) => x.colors.some((c) => colors.includes(c)));
          if (fabrics.length > 1) d = d.filter((x) => fabrics.includes(x.fabric));
          if (occasions.length > 1) d = d.filter((x) => x.occasions.some((o) => occasions.includes(o)));
          setProducts(d);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [params, colors, fabrics, occasions]);

  const toggle = (list, setList, value) =>
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

  const clearAll = () => {
    setPrice([0, 50000]);
    setColors([]);
    setFabrics([]);
    setOccasions([]);
    setInStockOnly(false);
    setSearchParams({});
  };

  const heading = q
    ? `Search: "${q}"`
    : category
      ? CATEGORY_LABELS[category]
      : badge === "new"
        ? "New Arrivals"
        : badge === "bestseller"
          ? "Best Sellers"
          : "All Sarees";

  useSEO({
    title: `${heading} — Buy Ilkal Sarees Online | R V Ilkal Sarees`,
    description: `Shop ${heading === "All Sarees" ? "authentic handloom Ilkal sarees" : heading.toLowerCase()} online — woven in Ilkal, Karnataka. Pan-India delivery, COD and secure online payment.`,
    canonicalPath: "/shop",
  });

  useEffect(() => {
    if (category || badge) metaTrack("ViewCategory", { content_category: heading, content_type: "product_group" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, badge]);

  const Filters = (
    <div className="space-y-2" data-testid="filters-panel">
      <Accordion type="multiple" defaultValue={["price", "colour", "fabric", "category"]}>
        <AccordionItem value="price" className="border-gold/20">
          <AccordionTrigger className="text-xs tracking-[0.18em] uppercase text-charcoal hover:no-underline">Price</AccordionTrigger>
          <AccordionContent>
            <div className="pt-3 pb-1 px-1">
              <Slider value={price} onValueChange={setPrice} min={0} max={50000} step={500} data-testid="price-slider" />
              <div className="flex justify-between text-xs text-charcoal/60 mt-3">
                <span data-testid="price-min-label">₹{price[0].toLocaleString("en-IN")}</span>
                <span data-testid="price-max-label">₹{price[1].toLocaleString("en-IN")}</span>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="colour" className="border-gold/20">
          <AccordionTrigger className="text-xs tracking-[0.18em] uppercase text-charcoal hover:no-underline">Colour</AccordionTrigger>
          <AccordionContent>
            <div className="flex flex-wrap gap-2.5 pt-2">
              {SHOP_COLORS.map((c) => (
                <button
                  key={c.name}
                  onClick={() => toggle(colors, setColors, c.name)}
                  data-testid={`filter-color-${c.name.toLowerCase()}`}
                  title={c.name}
                  className={`w-8 h-8 rounded-full border transition-all ${colors.includes(c.name) ? "ring-2 ring-maroon ring-offset-2 ring-offset-ivory" : "border-gold/40"}`}
                  style={{ backgroundColor: c.hex }}
                />
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="fabric" className="border-gold/20">
          <AccordionTrigger className="text-xs tracking-[0.18em] uppercase text-charcoal hover:no-underline">Fabric</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 pt-2">
              {FABRICS.map((f) => (
                <label key={f} className="flex items-center gap-3 text-sm text-charcoal/70 cursor-pointer">
                  <Checkbox
                    checked={fabrics.includes(f)}
                    onCheckedChange={() => toggle(fabrics, setFabrics, f)}
                    data-testid={`filter-fabric-${f.toLowerCase().replace(/\s+/g, "-")}`}
                    className="border-gold/50 data-[state=checked]:bg-maroon data-[state=checked]:border-maroon"
                  />
                  {f}
                </label>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="category" className="border-gold/20">
          <AccordionTrigger className="text-xs tracking-[0.18em] uppercase text-charcoal hover:no-underline">Collection</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-2 pt-2">
              {Object.entries(CATEGORY_LABELS).map(([slug, label]) => (
                <button
                  key={slug}
                  onClick={() => {
                    const next = new URLSearchParams(searchParams);
                    if (category === slug) next.delete("category"); else { next.set("category", slug); next.delete("badge"); }
                    setSearchParams(next);
                  }}
                  data-testid={`filter-category-${slug}`}
                  className={`block text-sm text-left w-full py-1 transition-colors ${category === slug ? "text-maroon font-semibold" : "text-charcoal/70 hover:text-maroon"}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="occasion" className="border-gold/20">
          <AccordionTrigger className="text-xs tracking-[0.18em] uppercase text-charcoal hover:no-underline">Occasion</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 pt-2">
              {OCCASIONS.map((o) => (
                <label key={o} className="flex items-center gap-3 text-sm text-charcoal/70 cursor-pointer">
                  <Checkbox
                    checked={occasions.includes(o)}
                    onCheckedChange={() => toggle(occasions, setOccasions, o)}
                    data-testid={`filter-occasion-${o.toLowerCase().replace(/\s+/g, "-")}`}
                    className="border-gold/50 data-[state=checked]:bg-maroon data-[state=checked]:border-maroon"
                  />
                  {o}
                </label>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="more" className="border-gold/20">
          <AccordionTrigger className="text-xs tracking-[0.18em] uppercase text-charcoal hover:no-underline">Availability & More</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 pt-2">
              <label className="flex items-center gap-3 text-sm text-charcoal/70 cursor-pointer">
                <Checkbox checked={inStockOnly} onCheckedChange={(v) => setInStockOnly(!!v)} data-testid="filter-instock" className="border-gold/50 data-[state=checked]:bg-maroon data-[state=checked]:border-maroon" />
                In Stock Only
              </label>
              <button
                onClick={() => { const n = new URLSearchParams(searchParams); n.set("badge", "new"); n.delete("category"); setSearchParams(n); }}
                data-testid="filter-new-arrivals"
                className={`block text-sm text-left ${badge === "new" ? "text-maroon font-semibold" : "text-charcoal/70 hover:text-maroon"}`}
              >
                New Arrivals
              </button>
              <button
                onClick={() => { const n = new URLSearchParams(searchParams); n.set("badge", "bestseller"); n.delete("category"); setSearchParams(n); }}
                data-testid="filter-best-sellers"
                className={`block text-sm text-left ${badge === "bestseller" ? "text-maroon font-semibold" : "text-charcoal/70 hover:text-maroon"}`}
              >
                Best Sellers
              </button>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <button onClick={clearAll} data-testid="clear-filters-btn" className="text-xs tracking-[0.15em] uppercase text-maroon underline underline-offset-4 pt-4">
        Clear All Filters
      </button>
    </div>
  );

  const chipActive = (params) => {
    if (params.category) return category === params.category && !badge;
    if (params.badge) return badge === params.badge && !category;
    return !category && !badge && !q;
  };

  const goChip = (params) => {
    const next = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => next.set(k, v));
    setSearchParams(next);
  };

  const CHIPS = [
    { label: "All Sarees", params: {} },
    { label: "New", params: { badge: "new" } },
    { label: "Best Sellers", params: { badge: "bestseller" } },
    ...Object.entries(CATEGORY_LABELS).map(([slug, label]) => ({ label: CHIP_LABELS[slug] || label, params: { category: slug } })),
  ];

  return (
    <main data-testid="shop-page">
      <div className="bg-maroon-dark text-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
          <nav className="text-[11px] tracking-wide text-ivory/50 mb-4" data-testid="shop-breadcrumb">
            <Link to="/" className="hover:text-gold transition-colors">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-gold">{heading}</span>
          </nav>
          <p className="text-[10px] sm:text-[11px] tracking-[0.26em] uppercase text-gold font-semibold">R V Ilkal Sarees · Collection</p>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium mt-2" data-testid="shop-heading">{heading}</h1>
          <p className="text-ivory/70 text-sm sm:text-base mt-3 max-w-xl font-light">
            Authentic Ilkal sarees woven in Ilkal, Karnataka — pure handloom, delivered across India.
          </p>
          <p className="text-[11px] tracking-[0.2em] uppercase text-gold/80 mt-4" data-testid="product-count">{products.length} sarees · Direct from Ilkal</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex flex-wrap gap-2 mb-8" data-testid="category-chips">
          {CHIPS.map((c) => (
            <button
              key={c.label}
              onClick={() => goChip(c.params)}
              data-testid={`chip-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`text-xs tracking-wide px-4 py-2 ring-1 transition-colors ${chipActive(c.params) ? "bg-maroon text-ivory ring-maroon" : "bg-white text-charcoal/70 ring-gold/30 hover:ring-gold hover:text-maroon"}`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div className="bg-white ring-1 ring-gold/20 px-4 sm:px-5 py-3 flex items-center justify-between gap-3 mb-8">
          <p className="text-sm text-charcoal/60">
            Showing <span className="font-semibold text-charcoal">{products.length}</span> sarees
          </p>
          <div className="flex items-center gap-2 sm:gap-3">
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-36 sm:w-52 border-gold/40 rounded-sm h-10 text-xs sm:text-sm" data-testid="sort-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-ivory border-gold/40 rounded-sm">
                {SORTS.map((s) => (
                  <SelectItem key={s.value} value={s.value} data-testid={`sort-${s.value}`}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <button className="flex items-center gap-2 bg-maroon text-ivory text-[11px] tracking-[0.15em] uppercase px-4 sm:px-5 py-2.5 hover:bg-maroon-dark transition-colors" data-testid="filters-btn">
                  <SlidersHorizontal className="w-4 h-4" strokeWidth={1.5} /> Filters
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-ivory w-80 overflow-y-auto p-6" data-testid="mobile-filters-sheet" aria-describedby="mobile-filters-desc">
                <span id="mobile-filters-desc" className="sr-only">Filter sarees by category, colour, fabric, occasion and price</span>
                <div className="flex items-center justify-between mb-4">
                  <p className="font-heading text-xl text-charcoal">Filters</p>
                  <button onClick={() => setFiltersOpen(false)} aria-label="Close filters"><X className="w-5 h-5 text-charcoal/60" /></button>
                </div>
                {Filters}
              </SheetContent>
            </Sheet>
          </div>
        </div>

        <div>

        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 sm:gap-8">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[3/4] bg-surface" />
                <div className="h-4 bg-surface mt-4 w-3/4" />
                <div className="h-3 bg-surface mt-2 w-1/2" />
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-24" data-testid="no-products">
            <p className="font-heading text-2xl text-charcoal">No sarees match your filters</p>
            <button onClick={clearAll} data-testid="no-products-clear-btn" className="mt-6 bg-maroon text-ivory text-xs tracking-[0.18em] uppercase px-8 py-3 hover:bg-maroon-dark">
              Clear Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 sm:gap-8" data-testid="product-grid">
            {products.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
        </div>
      </div>
    </main>
  );
}
